import { IconName, MemreButton, MemreFlexBox, MemreIcon } from 'components/core';
import React, { ChangeEvent, useRef, useState, useEffect } from 'react';
import { updateSound } from 'utils/updateSound';

export default function Sound({ add, url, handleDelete, iconOnly = false }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const doc = await updateSound(event.target.files[0]);
      add(doc.url, doc.id);
    }
  };

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (!url) return;

    if (!audio) {
      // Create a new audio instance if none exists
      const newAudio = new Audio(url);

      // Set up event handlers
      newAudio.onended = () => setIsPlaying(false);
      newAudio.onpause = () => setIsPlaying(false);
      newAudio.onplay = () => setIsPlaying(true);

      // Store the audio instance
      setAudio(newAudio);

      // Start playing
      newAudio.play();
      setIsPlaying(true);
    } else {
      // Toggle existing audio
      if (isPlaying) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
    }
  };

  // Clean up audio when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setAudio(null);
      }
    };
  }, [audio, url]);

  return (
    <>
      <input
        type="file"
        style={{ display: 'none' }}
        accept=".mp3, .wav, .aiff, .flac, .m4a, .aac, .ogg, .wma"
        onChange={handleFileChange}
        ref={fileInputRef}
      />
      {!url && !iconOnly && (
        <MemreButton text="Add Sound" iconName="sound" variant="outlined" size="small" onClick={handleButtonClick} />
      )}
      {!url && iconOnly && (
        <MemreButton variant="icon" iconName="sound" onClick={handleButtonClick} />
      )}
      {url && (
        <MemreFlexBox
          direction="row"
          sx={{
            position: 'relative',
            border: '1px solid #333',
            borderRadius: '4px',
            px: iconOnly ? 1 : 2,
            py: iconOnly ? 0.25 : 0.5,
            gap: 1,
          }}
          align="center"
          justify="space-between"
        >
          {/* Play/Pause Button */}
          <MemreButton
            variant="icon"
            iconName={isPlaying ? IconName.pause : IconName.play}
            onClick={handlePlayPause}
            sx={{
              width: iconOnly ? '28px' : '32px',
              height: iconOnly ? '28px' : '32px',
              minWidth: iconOnly ? '28px' : '32px',
              padding: 0,
            }}
          />

          {/* Delete Button */}
          <MemreButton
            variant="icon"
            iconName="trash"
            onClick={handleDelete}
            sx={{
              backgroundColor: 'white',
              opacity: 0.5,
              '&:hover': { opacity: 1 },
              width: iconOnly ? '24px' : '28px',
              height: iconOnly ? '24px' : '28px',
              minWidth: iconOnly ? '24px' : '28px',
              padding: 0,
            }}
          />
        </MemreFlexBox>
      )}
    </>
  );
}
